/** ckeditor5.css's root `.ck-content` styles, which the inline-styles export copies onto top-level elements. */
const CK_CONTENT_ROOT_STYLES =
  'font-family: Helvetica, Arial, Tahoma, Verdana, Sans-Serif; font-size: medium; color: #000; line-height: 1.5; ' +
  'word-break: normal; overflow-wrap: break-word';

/** ckeditor5.css's figure margins per image style; one of these on a figure is a default, not an author value. */
const IMAGE_STYLE_DEFAULT_MARGINS = [
  '0.9em auto',
  '0.9em 0 0.9em 1.5em',
  '0.9em 1.5em 0.9em 0',
  '0.9em auto 0.9em 0',
  '0.9em 0 0.9em auto',
];
const MARGIN_SIDES = ['margin-top', 'margin-right', 'margin-bottom', 'margin-left'];

export class WysiwygBase64Sanitizer {
  static removeBase64Images(html: string): string {
    return html.replace(/<img[^>]+src=["']data:image\/[^"']+["'][^>]*>/g, '').replace(/data:image\/[^;]+;base64,[A-Za-z0-9+/=]+/g, '');
  }

  static sanitizeInitialValue(value: string, block: boolean): string {
    return block && typeof value === 'string' ? this.removeBase64Images(value) : value;
  }

  /**
   * Inlines the image layout that CKEditor's raw data leaves to ckeditor5.css, for non-email content saved or shown
   * without that stylesheet: without it an <img width="970"> overflows its resized figure.
   */
  static addImageLayoutStyles(html: string): string {
    if (!html || !html.includes('<img')) return html;
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    this.applyImageLayoutStyles(doc);
    return doc.body.firstElementChild?.innerHTML ?? html;
  }

  /**
   * Only max-width, float and margin: General HTML Support keeps <img> styles in the editor, and on reload CKEditor
   * reads width/height styles back as the image's resize size, while min-width or display:block would stretch or
   * break the image once it is switched to inline.
   */
  private static applyImageLayoutStyles(doc: Document): void {
    // ckeditor5.css's `.image img { max-width: 100% }`
    doc.querySelectorAll<HTMLImageElement>('figure.image img').forEach((img) => {
      img.style.maxWidth = '100%';
    });
    // ckeditor5.css's figure layout per image style: `.image { display: table; margin: .9em auto }` centres by
    // default, `.image_resized { display: block; max-width: 100% }`, side and align-left/right float with a 1.5em
    // gap (side also capped at 50%), and block-align-left/right push the figure to one edge
    doc.querySelectorAll<HTMLElement>('figure.image').forEach((figure) => {
      const has = (cls: string) => figure.classList.contains(cls);
      if (has('image_resized')) {
        this.setIfUnset(figure.style, 'display', 'block');
        // a side figure is capped at half the width, as it was before the resized rule set max-width itself
        this.setIfUnset(figure.style, 'max-width', has('image-style-side') ? '50%' : '100%');
        this.setIfUnset(figure.style, 'box-sizing', 'border-box');
      } else {
        this.setIfUnset(figure.style, 'display', 'table');
      }
      if (has('image-style-side')) {
        figure.style.float = 'right';
        this.setIfUnset(figure.style, 'max-width', '50%');
        this.applyFigureMargin(figure, '0.9em 0 0.9em 1.5em');
      } else if (has('image-style-align-right')) {
        figure.style.float = 'right';
        this.applyFigureMargin(figure, '0.9em 0 0.9em 1.5em');
      } else if (has('image-style-align-left')) {
        figure.style.float = 'left';
        this.applyFigureMargin(figure, '0.9em 1.5em 0.9em 0');
      } else if (has('image-style-block-align-left')) {
        this.applyFigureMargin(figure, '0.9em auto 0.9em 0');
      } else if (has('image-style-block-align-right')) {
        this.applyFigureMargin(figure, '0.9em 0 0.9em auto');
      } else {
        this.applyFigureMargin(figure, '0.9em auto');
      }
    });
    // the displayed-size attributes pin the height while max-width scales the width down, which would squash the
    // image; ckeditor5.css overrides them with height:auto, so write it on block images. CKEditor upcasts a block
    // image's resize height from the figure's height style (the img's only for inline images), so the img's
    // height:auto is never read back as a resize height (ATLAS-5774). aspect-ratio alone does not prevent the
    // squash, as it is ignored once the height attribute and the capped width are both definite
    doc.querySelectorAll<HTMLImageElement>('img').forEach((img) => this.applyDisplayedSizeAttributes(img));
    doc.querySelectorAll<HTMLImageElement>('figure.image img').forEach((img) => this.setIfUnset(img.style, 'height', 'auto'));
  }

  /** Sets the property only when the author has not, so an author value is never overwritten. */
  private static setIfUnset(style: CSSStyleDeclaration, prop: string, value: string): void {
    if (!style.getPropertyValue(prop)) style.setProperty(prop, value);
  }

  /** True when all four margins are set and equal one of ckeditor5.css's per-image-style figure margins. */
  private static isImageStyleDefaultMargin(figure: HTMLElement): boolean {
    const current = MARGIN_SIDES.map((side) => figure.style.getPropertyValue(side));
    if (current.some((value) => !value)) return false;
    const scratch = document.createElement('div');
    return IMAGE_STYLE_DEFAULT_MARGINS.some((margin) => {
      scratch.style.margin = margin;
      return MARGIN_SIDES.every((side, i) => scratch.style.getPropertyValue(side) === current[i]);
    });
  }

  /**
   * Writes the class default margin, unless the author set one: a baked default from an earlier save is recomputed
   * for the element's current class, a partly set margin keeps its sides and gets the default on the others.
   */
  private static applyFigureMargin(element: HTMLElement, defaultMargin: string): void {
    if (!MARGIN_SIDES.some((side) => element.style.getPropertyValue(side)) || this.isImageStyleDefaultMargin(element)) {
      element.style.margin = defaultMargin;
      return;
    }
    const scratch = document.createElement('div');
    scratch.style.margin = defaultMargin;
    MARGIN_SIDES.forEach((side) => this.setIfUnset(element.style, side, scratch.style.getPropertyValue(side)));
  }

  /** True for the block-align figures (`image-style-block-align-left|right`); R3 wraps them with a cell-aligned table. */
  private static isBlockAlignFigure(figure: HTMLElement): boolean {
    return figure.classList.contains('image-style-block-align-left') || figure.classList.contains('image-style-block-align-right');
  }

  /** A centred figure: no float and not a block-align figure. */
  private static isCentredImageFigure(figure: HTMLElement): boolean {
    return !figure.style.float && !this.isBlockAlignFigure(figure);
  }

  /**
   * DOM counterpart of the guard's isEmailImageWrapper/getSoleCell (ckeditor-email-image-wrapper-guard.ts, which
   * works on CKEditor view nodes): the table's one row (with or without a tbody) holds one cell, which holds an img.
   */
  private static getSoleImageCell(table: HTMLTableElement): HTMLTableCellElement | null {
    const content = (el: Element) =>
      Array.from(el.childNodes).filter((n) => (n.nodeType === Node.TEXT_NODE ? /[^ \t\n\f\r]/.test(n.textContent ?? '') : n.nodeType === Node.ELEMENT_NODE));
    let children = content(table);
    if (children.length === 1 && (children[0] as Element).tagName === 'TBODY') children = content(children[0] as Element);
    if (children.length !== 1 || (children[0] as Element).tagName !== 'TR') return null;
    const cells = content(children[0] as Element);
    if (cells.length !== 1) return null;
    const cell = cells[0] as Element;
    if (cell.nodeType !== Node.ELEMENT_NODE || (cell.tagName !== 'TD' && cell.tagName !== 'TH') || !cell.querySelector('img')) return null;
    return cell as HTMLTableCellElement;
  }

  /**
   * Gives the figure `table[role=presentation] > tbody > tr > td` as its only child, holding all the figure's
   * content, with the given table and cell alignment attributes. A figure whose only element child is already such a
   * table (idempotency, or a figure whose style changed) keeps it and has its alignment attributes rewritten, so
   * there is never a second wrapper or a stale `align`/`width`.
   */
  private static wrapFigureContent(
    doc: Document,
    figure: HTMLElement,
    tableAttrs: Record<string, string>,
    cellAttrs: Record<string, string>
  ): void {
    const elements = Array.from(figure.children);
    const soleTable =
      elements.length === 1 && elements[0].tagName === 'TABLE' && elements[0].getAttribute('role') === 'presentation'
        ? (elements[0] as HTMLTableElement)
        : null;
    const soleCell = soleTable ? this.getSoleImageCell(soleTable) : null;
    // a presentation table that is not the wrapper shape (no cell, several cells, no image) is left as it is
    if (soleTable && !soleCell) return;
    let table: HTMLTableElement;
    let cell: HTMLTableCellElement;
    if (soleTable && soleCell) {
      table = soleTable;
      cell = soleCell;
      // stale alignment goes; attributes that stay are set in place, keeping their serialised order
      ['align', 'width'].filter((name) => !(name in tableAttrs)).forEach((name) => table.removeAttribute(name));
      if (!('align' in cellAttrs)) cell.removeAttribute('align');
    } else {
      table = doc.createElement('table');
      table.setAttribute('role', 'presentation');
      cell = doc.createElement('td');
      // exactly what the cell pass writes on a later run, so a second cleanup leaves the cell unchanged
      cell.setAttribute('valign', 'middle');
      Object.entries(cellAttrs).forEach(([name, value]) => cell.setAttribute(name, value));
      cell.style.verticalAlign = 'middle';
      while (figure.firstChild) cell.appendChild(figure.firstChild);
      const row = doc.createElement('tr');
      row.appendChild(cell);
      const body = doc.createElement('tbody');
      body.appendChild(row);
      table.appendChild(body);
      figure.appendChild(table);
    }
    Object.entries(tableAttrs).forEach(([name, value]) => table.setAttribute(name, value));
    Object.entries(cellAttrs).forEach(([name, value]) => cell.setAttribute(name, value));
  }

  /**
   * Writes the size a resized image is displayed at into its width/height attributes. The attributes are the file's
   * natural size, which overrides the resize in clients that ignore the CSS size (ATLAS-5715); an image that is not
   * resized keeps them, as they are the only size a CKEditor 4 image has (ATLAS-5791).
   */
  private static applyDisplayedSizeAttributes(img: HTMLImageElement): void {
    const resizedFigure = img.closest<HTMLElement>('figure.image_resized');
    if (!img.style.width && !resizedFigure) return;
    const px = (value: string) => {
      const match = /^(\d+(?:\.\d+)?)px$/.exec(value);
      return match ? parseFloat(match[1]) : null;
    };
    const attrWidth = parseFloat(img.getAttribute('width') ?? '');
    const attrHeight = parseFloat(img.getAttribute('height') ?? '');
    let ratio: number | null = null;
    if (attrWidth > 0 && attrHeight > 0) {
      ratio = attrWidth / attrHeight;
    } else {
      const parts = img.style.aspectRatio.split('/').map((part) => parseFloat(part));
      if (parts.length <= 2 && parts.every((part) => part > 0)) ratio = parts[0] / (parts[1] ?? 1);
    }
    const width = px(img.style.width) ?? (resizedFigure ? px(resizedFigure.style.width) : null);
    if (width === null) {
      // a legacy % resize: natural-size attributes would override it
      img.removeAttribute('width');
      img.removeAttribute('height');
      return;
    }
    img.setAttribute('width', String(Math.round(width)));
    const height = px(img.style.height) ?? (ratio ? width / ratio : null);
    if (height === null) img.removeAttribute('height');
    else img.setAttribute('height', String(Math.round(height)));
  }

  /**
   * @param forEmail also size images with explicit widths and add align attributes, for email clients
   *   that ignore the figure's CSS width and margin:auto
   */
  static cleanExportedImageStyles(html: string, forEmail = false): string {
    if (!html) return html;
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    doc.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
      // must run before aspect-ratio is removed below, as the ratio can be the only source of the height
      this.applyDisplayedSizeAttributes(img);
      img.style.removeProperty('max-width');
      img.style.removeProperty('min-width');
      img.style.removeProperty('aspect-ratio');
    });
    doc.querySelectorAll<HTMLElement>('figure.image-style-side, figure.image-style-align-right').forEach((figure) => {
      if (figure.style.float) return; // a figure saved before already carries it
      const s = (figure.getAttribute('style') ?? '').replace(/;\s*$/, '');
      figure.setAttribute('style', s ? `${s};float:right` : 'float:right');
    });
    doc.querySelectorAll<HTMLElement>('figure.image-style-align-left').forEach((figure) => {
      if (figure.style.float) return;
      const s = (figure.getAttribute('style') ?? '').replace(/;\s*$/, '');
      figure.setAttribute('style', s ? `${s};float:left` : 'float:left');
    });
    // CKEditor never outputs its default cell alignment (middle), relying on the browser default; email
    // clients and host stylesheets often default cells to top, so write the alignment the editor showed
    doc.querySelectorAll<HTMLElement>('td, th').forEach((cell) => {
      const verticalAlign = cell.style.verticalAlign || cell.getAttribute('valign') || 'middle';
      cell.style.verticalAlign = verticalAlign;
      // Outlook desktop only honours the valign attribute
      if (forEmail) cell.setAttribute('valign', verticalAlign);
    });
    // ckeditor5.css's `.image { display: table; margin: .9em auto }` and `.image_resized { display: block;
    // max-width: 100%; box-sizing: border-box }` are not inlined (the export transformation clears them);
    // without them centred images lose their centring and the figure gets the default margin of 1em 40px
    doc.querySelectorAll<HTMLElement>('figure.image').forEach((figure) => {
      if (figure.classList.contains('image_resized')) {
        this.setIfUnset(figure.style, 'display', 'block');
        // email content never had the side cap (its layout pass is skipped), so it keeps 100%
        const sideCap = !forEmail && figure.classList.contains('image-style-side');
        this.setIfUnset(figure.style, 'max-width', sideCap ? '50%' : '100%');
        this.setIfUnset(figure.style, 'box-sizing', 'border-box');
      } else {
        this.setIfUnset(figure.style, 'display', 'table');
      }
      if (figure.style.float === 'right') this.applyFigureMargin(figure, '0.9em 0 0.9em 1.5em');
      else if (figure.style.float === 'left') this.applyFigureMargin(figure, '0.9em 1.5em 0.9em 0');
      else this.applyFigureMargin(figure, '0.9em auto');
    });
    // email content is sized below instead
    if (!forEmail) this.applyImageLayoutStyles(doc);
    // after the export transformations run, CKEditor copies ckeditor5.css's root `.ck-content` styles onto every
    // top-level element, so every element carrying an inline style is checked. Headings (font-size: medium flattens
    // them to body text, ATLAS-5760) and <hr> (kept bare as CKEditor 4 saved it, ATLAS-5768) are special cases of
    // that rule. A value the author set differs from the root value and is kept
    const rootStyles = doc.createElement('div').style;
    rootStyles.cssText = CK_CONTENT_ROOT_STYLES;
    doc.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
      // removing a property re-serialises the style through the CSSOM, which drops declarations the browser does
      // not recognise; Outlook's mso-* ones (e.g. mso-line-height-rule) survive the export, so carry them over
      const mso = (el.getAttribute('style') ?? '')
        .split(';')
        .map((d) => d.trim())
        .filter((d) => /^mso-/i.test(d));
      let removed = false;
      for (let i = 0; i < rootStyles.length; i++) {
        const name = rootStyles[i];
        if (el.style.getPropertyValue(name) === rootStyles.getPropertyValue(name)) {
          el.style.removeProperty(name);
          removed = true;
        }
      }
      if (mso.length) {
        if (removed) el.setAttribute('style', `${el.style.cssText} ${mso.join(';')};`.trim());
      } else if (!el.style.length) el.removeAttribute('style');
    });
    // ATLAS-5884: the export marks author-centred tables (data-export-align); email gets Outlook's align, the marker never stays
    doc.querySelectorAll<HTMLElement>('[data-export-align]').forEach((el) => {
      if (forEmail && el.getAttribute('data-export-align') === 'center') el.setAttribute('align', 'center');
      el.removeAttribute('data-export-align');
    });
    if (forEmail) {
      // the figure carries the editor's resized width; put it on the image itself, or the image renders at
      // the file's natural size (often 2x for Canva) and spills out of its table cell
      doc.querySelectorAll<HTMLImageElement>('figure.image img').forEach((img) => {
        const px = /^(\d+(?:\.\d+)?)px$/.exec(img.closest('figure')!.style.width);
        if (px && !img.style.width) {
          const width = Math.round(parseFloat(px[1]));
          img.style.width = `${width}px`;
          // Outlook desktop ignores CSS image widths and only honours the attribute
          img.setAttribute('width', String(width));
        }
        // rewritten in a fixed order, so a second cleanup serialises the style identically
        const height = img.style.height || 'auto';
        img.style.removeProperty('height');
        img.style.removeProperty('display');
        img.style.maxWidth = '100%';
        img.style.height = height;
        img.style.display = 'block';
      });
      // Outlook desktop ignores margin:auto, even inlined; the legacy align attribute is what it centres on
      doc.querySelectorAll<HTMLElement>('.table-style-align-center').forEach((el) => {
        const table = el.tagName === 'TABLE' ? el : el.querySelector(':scope > table');
        table?.setAttribute('align', 'center');
      });
      // Outlook desktop ignores margin:auto and float on figures: centred figures get a centring table around
      // their content, left / right / side figure images the legacy align attribute (ATLAS-5882)
      doc.querySelectorAll<HTMLElement>('figure.image').forEach((figure) => {
        if (this.isCentredImageFigure(figure)) {
          this.wrapFigureContent(doc, figure, { align: 'center' }, {});
        } else if (this.isBlockAlignFigure(figure)) {
          // align goes on the cell, never the table: Outlook floats a table that has align=left|right
          const side = figure.classList.contains('image-style-block-align-left') ? 'left' : 'right';
          this.wrapFigureContent(doc, figure, { width: '100%' }, { align: side });
        }
      });
      doc.querySelectorAll<HTMLImageElement>('figure.image img').forEach((img) => {
        const figure = img.closest('figure.image')!;
        if (figure.classList.contains('image-style-align-left')) img.setAttribute('align', 'left');
        else if (figure.classList.contains('image-style-align-right') || figure.classList.contains('image-style-side')) {
          img.setAttribute('align', 'right');
        } else img.removeAttribute('align'); // stale from an earlier save of another style
      });
      // an inline image that lost its align class loses the align and float pair R1.7 wrote for it, nothing else
      doc.querySelectorAll<HTMLImageElement>('img:not(.image-style-align-left):not(.image-style-align-right)').forEach((img) => {
        if (img.closest('figure.image')) return;
        // R1.7 writes align and float together, so only an align of left or right that equals the img's own float
        // is the pair it wrote; any other align or float is the author's and stays
        const align = img.getAttribute('align');
        if ((align === 'left' || align === 'right') && img.style.float === align) {
          img.removeAttribute('align');
          img.style.removeProperty('float');
          if (!img.getAttribute('style')) img.removeAttribute('style');
        }
      });
      // inline images aligned left / right are a bare <img class="image-style-align-*">; the class decides
      // align, float and margin, as the figure transformation does for figures
      doc.querySelectorAll<HTMLImageElement>('img.image-style-align-left, img.image-style-align-right').forEach((img) => {
        if (img.closest('figure.image')) return;
        const left = img.classList.contains('image-style-align-left');
        img.setAttribute('align', left ? 'left' : 'right');
        img.style.float = left ? 'left' : 'right';
        this.applyFigureMargin(img, left ? '0.9em 1.5em 0.9em 0' : '0.9em 0 0.9em 1.5em');
      });
    }
    return doc.body.firstElementChild?.innerHTML ?? html;
  }

  /**
   * @param listen optional registrar so callers (e.g. a pooled editor lease) can remove the listeners later
   */
  static attachImageBlockingEvents(
    editor: any,
    block: boolean,
    listen: (target: any, event: string, callback: Function, options?: any) => void = (target, event, callback, options) =>
      target.on(event, callback, options)
  ): void {
    if (!block) return;

    // Block file uploads
    listen(editor, 'fileUploadRequest', (evt: any) => {
      const fileLoader = evt.data.fileLoader;
      const file = fileLoader.file;
      if (file?.type?.startsWith('image/')) {
        evt.cancel();
      }
    });

    // Block pasted image data
    listen(
      editor,
      'paste',
      (evt: any) => {
        const data = evt.data;
        if (data.dataTransfer?._.files?.length > 0) {
          const hasImage = Array.from(data.dataTransfer._.files).some((f: File) => f.type.startsWith('image/'));
          if (hasImage) {
            evt.cancel();
          }
        }

        if (data.dataValue?.includes('src="data:image/')) {
          data.dataValue = this.removeBase64Images(data.dataValue);
        }
      },
      this
    );

    // Block dropped images
    listen(editor, 'drop', (evt: any) => {
      if (evt.data?.dataTransfer?._.files?.length > 0) {
        const hasImage = Array.from(evt.data.dataTransfer._.files).some((f: File) => f.type.startsWith('image/'));
        if (hasImage) {
          evt.cancel();
        }
      }
    });
  }
}
